"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, AlertCircle, Lock } from "lucide-react"
import { DownloadDatepickerDialog } from "@/components/shared/download-datepicker-dialog"
import { CustomDateRangeDialog } from "@/components/shared/custom-daterange-dialog"
import type { DateRange } from "react-day-picker"
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns"
import { slipVerificationAPI, type SlipVerificationFilters } from "@/src/lib/api/slip-verification-history"
import { v3VerificationHistoryAPI, type V3HistoryFilters } from "@/lib/api/v3-verification-history"
import { useAuth } from "@/src/contexts/AuthContext"

// Unified display row — normalized from both v2 (slip_transactions) and v3 (verifications)
interface DisplayRow {
  id: string
  branch_name: string | null       // v2: branch_name | v3: null
  sender_name: string | null       // v2: sender_account_name | v3: sender_bank
  sender_account: string | null    // v2: sender_account_number | v3: null
  receiver_name: string | null
  receiver_account: string | null
  amount: number
  verification_method: string      // 'API_V2' | 'API_V3' | 'LINE_CONNECT'
  status: string                   // v2: 'verified'|'duplicate'|'failed' | v3: 'success'|'failed'
  created_at: string
  credits_used: number
  original_transaction?: import('@/src/lib/api/slip-verification-history').OriginalTransactionData | null
}

type MethodTab = 'MANUAL' | 'API' | 'LINE'

export default function HistoryChecksPage() {
  const { hasPermission } = useAuth()
  const canExport = hasPermission('reports:export')
  
  // UI State
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isCustomDateRangeOpen, setIsCustomDateRangeOpen] = useState(false)
  
  // Data State
  const [rows, setRows] = useState<DisplayRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  
  // Filter State
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedMethod, setSelectedMethod] = useState<MethodTab>('MANUAL')
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'custom'>('today')
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined)
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 10

  // Build date range from current filter state
  const buildDateFilters = () => {
    const now = new Date()
    if (dateFilter === 'today') return { startDate: format(startOfDay(now), 'yyyy-MM-dd'), endDate: format(endOfDay(now), 'yyyy-MM-dd') }
    if (dateFilter === 'week') return { startDate: format(startOfWeek(now), 'yyyy-MM-dd'), endDate: format(endOfWeek(now), 'yyyy-MM-dd') }
    if (dateFilter === 'month') return { startDate: format(startOfMonth(now), 'yyyy-MM-dd'), endDate: format(endOfMonth(now), 'yyyy-MM-dd') }
    if (dateFilter === 'custom' && customDateRange?.from) {
      return {
        startDate: format(customDateRange.from, 'yyyy-MM-dd'),
        endDate: customDateRange.to ? format(customDateRange.to, 'yyyy-MM-dd') : undefined
      }
    }
    return {}
  }

  // Load data — API tab uses v3 endpoint, MANUAL/LINE use v2
  const loadTransactions = async () => {
    setLoading(true)
    setError(null)
    const dates = buildDateFilters()
    try {
      if (selectedMethod === 'API') {
        // v3: reads from `verifications` table (no business_id, direct API connect)
        const filters: V3HistoryFilters = { page: currentPage, limit, ...dates }
        const response = await v3VerificationHistoryAPI.getVerifications(filters)
        const data = response?.data?.history || []
        const pagination = response?.data?.pagination || { totalPages: 1, totalItems: 0 }
        setRows(data.map(v => {
          if (v.type === 'generation') {
            // /api/v3/generate/* records
            const qrLabel = v.method === 'promptpay' ? 'PromptPay' : v.method === 'bill-payment' ? 'Bill Payment' : v.method
            return {
              id: v.id,
              branch_name: qrLabel,                          // 1st col: QR type
              sender_name: null,                             // no sender for generation
              sender_account: null,
              receiver_name: v.accountType || v.billerId || null,   // promptpay: accountType, bill: billerId
              receiver_account: null,
              amount: v.amount ?? 0,
              verification_method: 'API_V3_GEN',
              status: v.status,
              created_at: v.createdAt,
              credits_used: v.credits ?? 1
            }
          }
          // /api/v3/verify/* records
          return {
            id: v.id,
            branch_name: v.senderBank || null,         // 1st col: ธนาคารผู้โอน
            sender_name: v.senderName || null,         // 2nd col: ผู้โอน (person name)
            sender_account: v.senderAccount || null,   // 2nd col sub: account number
            receiver_name: v.receiverName || null,
            receiver_account: v.receiverAccount || null,
            amount: v.amount ?? 0,
            verification_method: 'API_V3',
            status: v.status,
            created_at: v.createdAt,
            credits_used: v.credits ?? 1
          }
        }))
        setTotalPages(pagination.totalPages || 1)
        setTotal(pagination.totalItems || 0)
      } else {
        // v2: reads from `slip_transactions` table (business-linked)
        const filters: SlipVerificationFilters = {
          page: currentPage,
          limit,
          search: searchQuery || undefined,
          channel: selectedMethod === 'MANUAL' ? 'MANUAL' : 'LINE',
          ...dates
        }
        const response = await slipVerificationAPI.getSlipVerifications(filters)
        const data = response?.data?.verifications || []
        const pagination = response?.data?.pagination || { total_pages: 1, total_records: 0 }
        setRows(data.map(v => ({
          id: v.id,
          branch_name: v.branch_name || null,
          sender_name: v.sender_account_name || null,
          sender_account: v.sender_account_number || null,
          receiver_name: v.receiver_account_name || null,
          receiver_account: v.receiver_account_number || null,
          amount: typeof v.amount === 'string' ? parseFloat(v.amount) : (v.amount ?? 0),
          verification_method: v.verification_method || 'API_V2',
          status: v.verification_status,
          created_at: v.created_at,
          credits_used: v.credits_used,
          original_transaction: v.original_transaction || null
        })))
        setTotalPages(pagination.total_pages || 1)
        setTotal(pagination.total_records || 0)
      }
    } catch (err: any) {
      console.error('Failed to load:', err)
      setError(err.response?.data?.error?.message || 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง')
      setRows([])
    } finally {
      setLoading(false)
    }
  }
  
  // Load data on mount and when filters change
  useEffect(() => {
    loadTransactions()
  }, [currentPage, searchQuery, selectedMethod, dateFilter, customDateRange])
  
  // Event Handlers
  const handleSearch = () => {
    setSearchQuery(searchInput)
    setCurrentPage(1)
  }
  
  const handleMethodChange = (value: string) => {
    setSelectedMethod(value as MethodTab)
    setCurrentPage(1)
  }
  
  const handleDateFilterChange = (value: string) => {
    setDateFilter(value as 'today' | 'week' | 'month' | 'custom')
    setCurrentPage(1)
  }
  
  const handleCustomDateRangeConfirm = (dateRange: DateRange | undefined) => {
    setCustomDateRange(dateRange)
    setDateFilter('custom')
    setCurrentPage(1)
  }

  const handleDownloadConfirm = async (dateRange: DateRange | undefined) => {
    if (selectedMethod === 'API') return // v3 export not supported yet
    try {
      setIsExporting(true)
      
      // Build filters for export
      const filters: SlipVerificationFilters = {
        search: searchQuery || undefined,
        channel: selectedMethod === 'MANUAL' ? 'MANUAL' : 'LINE'
      }
      
      if (dateRange?.from) {
        filters.startDate = format(dateRange.from, 'yyyy-MM-dd')
        if (dateRange.to) {
          filters.endDate = format(dateRange.to, 'yyyy-MM-dd')
        }
      }
      
      const blob = await slipVerificationAPI.exportSlipVerifications('excel', filters)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transactions_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Failed to export:', err)
      setError('ไม่สามารถดาวน์โหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setIsExporting(false)
    }
  }
  
  // Pagination Handlers
  const goToFirstPage = () => setCurrentPage(1)
  const goToPrevPage = () => setCurrentPage(Math.max(1, currentPage - 1))
  const goToNextPage = () => setCurrentPage(Math.min(totalPages, currentPage + 1))
  const goToLastPage = () => setCurrentPage(totalPages)
  
  // Format helpers
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'verified' || status === 'success') return { label: '✓ ตรวจสอบแล้ว', classes: 'bg-green-100 text-green-700' }
    if (status === 'duplicate') return { label: '⚠ ซ้ำ', classes: 'bg-yellow-100 text-yellow-700' }
    return { label: '✗ ล้มเหลว', classes: 'bg-red-100 text-red-700' }
  }
  
  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm')
    } catch {
      return dateStr
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">ประวัติการตรวจสอบสลิป</h1>
          <p className="text-sm text-gray-500"></p>
        </div>

        {/* Verification Method Tabs */}
        <TabGroup value={selectedMethod} onValueChange={handleMethodChange} color="brand-blue" fullWidth>
          <TabGroupList>
            <TabGroupTrigger value="MANUAL">MANUAL</TabGroupTrigger>
            <TabGroupTrigger value="API">API{selectedMethod === 'API' && total > 0 ? ` (${total})` : ''}</TabGroupTrigger>
            <TabGroupTrigger value="LINE">LINE Connect</TabGroupTrigger>
          </TabGroupList>
        </TabGroup>

        {/* Search and Filter Section */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search Bar */}
          <div className="flex gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input 
                type="text" 
                placeholder="ชื่อสาขา, ชื่อผู้โอน, เลขบัญชีผู้โอน" 
                className="pl-10 h-11"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button 
              variant="secondary" 
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 h-11"
              onClick={handleSearch}
            >
              ค้นหา
            </Button>
          </div>

          {/* Date Filters and Download */}
          <div className="flex gap-2 items-center flex-wrap">
            <TabGroup value={dateFilter} onValueChange={handleDateFilterChange} size="md" color="brand-light-red">
              <TabGroupList>
                <TabGroupTrigger value="today">วันนี้</TabGroupTrigger>
                <TabGroupTrigger value="week">สัปดาห์นี้</TabGroupTrigger>
                <TabGroupTrigger value="month">เดือนนี้</TabGroupTrigger>
                <button
                  type="button"
                  className="px-4 py-2 font-medium text-gray-700 hover:bg-brand-light-red/10 rounded-lg transition-colors"
                  onClick={() => setIsCustomDateRangeOpen(true)}
                >
                  กำหนดเอง
                </button>
              </TabGroupList>
            </TabGroup>
            {selectedMethod !== 'API' && (
              <>
                <Button 
                  className="bg-brand-light-red hover:bg-brand-light-red/90 text-white h-11"
                  onClick={() => setIsDatePickerOpen(true)}
                  disabled={isExporting || !canExport}
                  title={!canExport ? "Enterprise package required" : ""}
                >
                  {!canExport && <Lock className="h-4 w-4 mr-2" />}
                  {isExporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      กำลังดาวน์โหลด...
                    </>
                  ) : (
                    'ดาวน์โหลดเอกสาร'
                  )}
                </Button>
                {!canExport && (
                  <p className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                    <Lock className="h-3 w-3" />
                    <span>ดาวน์โหลดรายงาน เป็นฟีเจอร์สำหรับ <strong>Enterprise Package</strong> เท่านั้น</span>
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-gray-700">{selectedMethod === 'API' ? 'ธนาคารผู้โอน' : 'สาขา'}</TableHead>
                <TableHead className="text-gray-700">ผู้โอน</TableHead>
                <TableHead className="text-gray-700">ผู้รับ</TableHead>
                <TableHead className="text-gray-700">จำนวนเงิน</TableHead>
                <TableHead className="text-gray-700">ช่องทาง</TableHead>
                <TableHead className="text-gray-700">สถานะ</TableHead>
                <TableHead className="text-gray-700">วันที่ส่งตรวจ</TableHead>
                <TableHead className="text-gray-700">เครดิตที่หัก</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                    <p className="text-gray-500 mt-2">กำลังโหลดข้อมูล...</p>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                    ไม่มีข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const statusBadge = getStatusBadge(row.status)
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.branch_name || row.sender_name || '-'}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{row.sender_name || '-'}</div>
                          <div className="text-gray-500">{row.sender_account || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{row.receiver_name || '-'}</div>
                          <div className="text-gray-500">{row.receiver_account || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">฿{formatAmount(row.amount)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          row.verification_method === 'API_V3' ? 'bg-blue-100 text-blue-700' :
                          row.verification_method === 'API_V3_GEN' ? 'bg-indigo-100 text-indigo-700' :
                          row.verification_method === 'API_V2' ? 'bg-purple-100 text-purple-700' :
                          row.verification_method === 'LINE_CONNECT' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {row.verification_method === 'API_V2' ? 'MANUAL' :
                           row.verification_method === 'API_V3' ? 'API Verify' :
                           row.verification_method === 'API_V3_GEN' ? 'QR Gen' :
                           row.verification_method === 'LINE_CONNECT' ? 'LINE' : 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.classes}`}>
                            {statusBadge.label}
                          </span>
                          {row.status === 'duplicate' && row.original_transaction && (
                            <div className="text-xs text-gray-500 pl-1 border-l-2 border-yellow-300 space-y-0.5">
                              <div className="font-medium text-yellow-700">รายการต้นฉบับ:</div>
                              <div>{row.original_transaction.sender_account_name || '-'}</div>
                              <div className="text-gray-400">{row.original_transaction.sender_account_number || ''}</div>
                              <div>฿{new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(parseFloat(String(row.original_transaction.amount ?? 0)))}</div>
                              <div className="text-gray-400">
                                {row.original_transaction.created_at
                                  ? (() => { try { return format(new Date(row.original_transaction.created_at), 'dd/MM/yyyy HH:mm') } catch { return row.original_transaction.created_at } })()
                                  : '-'}
                              </div>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      <TableCell className="text-right">{row.credits_used}</TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-2 py-4 border-t">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-gray-400"
              onClick={goToFirstPage}
              disabled={currentPage === 1 || loading}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-gray-400"
              onClick={goToPrevPage}
              disabled={currentPage === 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="icon" className="h-8 w-8 bg-brand-primary hover:bg-brand-primary/90 text-white">
              {currentPage}
            </Button>
            {totalPages > 1 && (
              <span className="text-sm text-gray-500 px-2">/ {totalPages}</span>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-gray-400"
              onClick={goToNextPage}
              disabled={currentPage === totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-gray-400"
              onClick={goToLastPage}
              disabled={currentPage === totalPages || loading}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Download Date Picker Dialog */}
      <DownloadDatepickerDialog
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
        onConfirm={handleDownloadConfirm}
      />

      <CustomDateRangeDialog
        open={isCustomDateRangeOpen}
        onOpenChange={setIsCustomDateRangeOpen}
        onConfirm={handleCustomDateRangeConfirm}
      />
    </DashboardLayout>
  )
}
